// The background picker (ClikMeet): one grid of choices, in the lobby before joining and from the camera menu in the
// call. A choice shows at once on the camera it is given and is remembered for the next meeting on this device.
import { apply, CHOICES, LIGHTING, lighting, ownPicture, processorFor, remember, rememberLighting, saved, supported } from "./background.js";
import { h, modal, toast } from "./dom.js";

// `getTrack()`: the camera track now (it changes when the camera is turned off and on in a call).
export async function pickBackground(getTrack) {
  if (!supported()) {
    toast("This browser cannot change your background — try the latest Chrome, Edge or Safari.");
    return;
  }
  let { id: chosen, own } = saved();
  let light = lighting();
  const grid = h("div", { class: "bg-grid", role: "radiogroup", "aria-label": "Background" });
  // Auto lighting (Ravi, 28 Sep): a switch under the backgrounds, applied to the camera at once and remembered.
  const lightRow = h("div", { class: "bg-lighting", role: "radiogroup", "aria-label": "Lighting" });
  const file = h("input", { type: "file", accept: "image/jpeg,image/png,image/webp", hidden: true });
  const status = h("p", { class: "bg-status", role: "status" });

  async function choose(id, ownUrl = own) {
    const track = getTrack();
    status.textContent = id === "none" && light === "off" ? "" : "Applying…";
    try {
      const ok = await apply(track, id, ownUrl, light);
      if (track && !ok && id !== "none") throw new Error("not shown");
      chosen = id;
      if (id === "own") own = ownUrl;
      const kept = remember(id, id === "own" ? ownUrl : undefined);
      status.textContent = !kept ? "Shown now — but this device could not keep it for your next meeting."
        : track ? "" : "Saved — it shows when your camera is on.";
    } catch {
      status.textContent = "That background could not be shown on this device.";
    }
    paint();
  }

  function option(id, label, face) {
    const b = h("button", { class: "bg-option" + (chosen === id ? " is-on" : ""), type: "button", role: "radio",
      "aria-checked": String(chosen === id), "aria-label": label, onclick: () => choose(id) }, face, h("span", { text: label }));
    return b;
  }

  async function chooseLight(id) {
    light = id;
    rememberLighting(id);
    paintLight();
    await choose(chosen, own);
  }
  function paintLight() {
    lightRow.replaceChildren(h("span", { class: "bg-lighting-label", text: "Lighting" }),
      ...LIGHTING.map((l) => h("button", { class: "bg-switch" + (light === l.id ? " is-on" : ""), type: "button", role: "radio",
        "aria-checked": String(light === l.id), text: l.label, onclick: () => chooseLight(l.id) })));
  }

  function paint() {
    paintLight();
    grid.replaceChildren(
      ...CHOICES.map((c) => option(c.id, c.label, c.thumb ? h("img", { src: c.thumb, alt: "", loading: "lazy" })
        : h("span", { class: `bg-face bg-${c.id}` }))),
      ...(own ? [option("own", "Your picture", h("img", { src: own, alt: "" }))] : []),
      h("button", { class: "bg-option bg-add", type: "button", onclick: () => file.click() }, h("span", { class: "bg-face bg-plus", text: "+" }), h("span", { text: own ? "Change your picture" : "Add your own" })),
    );
  }

  file.addEventListener("change", async () => {
    const f = file.files?.[0];
    file.value = "";
    if (!f) return;
    try { await choose("own", await ownPicture(f)); } catch (err) { status.textContent = err.message || "That picture could not be used."; }
  });

  paint();
  await modal("Background and lighting", h("div", { class: "bg-picker" }, grid, file, lightRow, status,
    h("p", { class: "bg-note", text: "Auto lighting brightens a dark room on your camera. A picture you add is kept on this device only." })),
  [{ text: "Done", value: true, class: "btn" }]);
}

// The remembered background as a processor, to give a camera BEFORE it is published (call.js toggleCam). Null: none.
export async function rememberedProcessor() {
  const { id, own } = saved();
  try { return await processorFor(id, own); } catch { return null; }
}

// On joining, and whenever the camera is turned back on: the remembered background, quietly.
export async function restoreBackground(track) {
  const { id, own } = saved();
  if (!track || !supported() || (id === "none" && lighting() === "off")) return;
  try { await apply(track, id, own, lighting()); } catch { /* the plain camera is shown instead */ }
}
